/**
 * Overview - Dashboard home page with key metrics
 */
import {
  AlertCircle,
  BookOpen,
  CheckCircle,
  Clock,
  Database,
  FileQuestion,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface OverviewProps {
  onNavigate: (section: string) => void;
}

interface Stats {
  totalExams: number;
  totalSubjects: number;
  totalQuizzes: number;
  totalQuestions: number;
  learningLessons: number;
  learningSkills: number;
  apiStatus: 'ok' | 'error';
  supabaseStatus: 'connected' | 'disconnected';
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const Overview: React.FC<OverviewProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load health check
      const healthRes = await fetch(`${API_BASE_URL}/health`);
      const health = await healthRes.json();

      // Load exams
      const examsRes = await fetch(`${API_BASE_URL}/api/exams`);
      const exams = await examsRes.json();

      // Load learning stats
      const learningRes = await fetch(`${API_BASE_URL}/api/learning/stats/program`);
      const learning = await learningRes.json();

      setStats({
        totalExams: exams.exams?.length || 0,
        totalSubjects: exams.exams?.reduce((acc: number, e: any) => acc + (e.subjects_count || 0), 0) || 0,
        totalQuizzes: 0, // Will be loaded separately if needed
        totalQuestions: 0,
        learningLessons: learning.stats?.total_lessons || 0,
        learningSkills: learning.stats?.total_skills || 0,
        apiStatus: health.status === 'healthy' ? 'ok' : 'error',
        supabaseStatus: health.supabase_configured ? 'connected' : 'disconnected',
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vue d'ensemble</h1>
          <p className="text-gray-500">Bienvenue sur le dashboard ConcoursCI</p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* API Status */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm">Statut API</span>
            {stats?.apiStatus === 'ok' ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
          <p className={`text-2xl font-bold ${stats?.apiStatus === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
            {stats?.apiStatus === 'ok' ? 'En ligne' : 'Hors ligne'}
          </p>
        </div>

        {/* Supabase Status */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm">Supabase</span>
            <Database className="w-5 h-5 text-gray-400" />
          </div>
          <p className={`text-2xl font-bold ${stats?.supabaseStatus === 'connected' ? 'text-green-600' : 'text-yellow-600'}`}>
            {stats?.supabaseStatus === 'connected' ? 'Connecté' : 'Déconnecté'}
          </p>
        </div>

        {/* Exams */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm">Concours</span>
            <BookOpen className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalExams || 0}</p>
        </div>

        {/* Learning Lessons */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm">Leçons CAFOP</span>
            <FileQuestion className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.learningLessons || 0}</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Learning System */}
        <div
          onClick={() => onNavigate('learning')}
          className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition transform hover:-translate-y-1"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Système d'Apprentissage</h3>
              <p className="text-blue-100 text-sm">Programme CAFOP complet</p>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>{stats?.learningSkills || 0} compétences</span>
            <span>{stats?.learningLessons || 0} leçons</span>
          </div>
        </div>

        {/* Question Generator */}
        <div
          onClick={() => onNavigate('questions')}
          className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition transform hover:-translate-y-1"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <FileQuestion className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Générateur de Questions</h3>
              <p className="text-green-100 text-sm">Vision GPT-4.1</p>
            </div>
          </div>
          <p className="text-sm">Générez des questions à partir d'images</p>
        </div>

        {/* Database Management */}
        <div
          onClick={() => onNavigate('database')}
          className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition transform hover:-translate-y-1"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Base de Données</h3>
              <p className="text-orange-100 text-sm">Gestion Supabase</p>
            </div>
          </div>
          <p className="text-sm">Gérez vos données et tables</p>
        </div>
      </div>

      {/* Recent activity placeholder */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="font-semibold text-lg text-gray-900 mb-4">Activité récente</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Système d'apprentissage CAFOP déployé</span>
            <span className="text-gray-400 ml-auto">Aujourd'hui</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Dashboard admin créé</span>
            <span className="text-gray-400 ml-auto">Aujourd'hui</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
